export const APP_COMPANIES = [
  'BONDSWAP',
  'BONDTRADER',
  'DORA',
  'NAVION',
  'ALLFX',
  'BLKBOX',
  'PAI2',
  'MAXOS',
  'POLYTRADE',
  'AFRICATRADES',
  'GOLDI',
  'PRIMEXM',
  'RUNALABS',
] as const

export type AppCompany = (typeof APP_COMPANIES)[number]

export const DEFAULT_APP_COMPANY: AppCompany = 'DORA'

export const APP_COMPANY_DISPLAY_NAMES: Record<AppCompany, string> = {
  BONDSWAP: 'Bondswap',
  BONDTRADER: 'BondTrader',
  DORA: 'DORA',
  NAVION: 'Navion',
  ALLFX: 'allFX-Consult',
  BLKBOX: 'BlackBox',
  PAI2: 'PAI2',
  MAXOS: 'Maxos',
  POLYTRADE: 'Polytrade',
  AFRICATRADES: 'Africatrades',
  GOLDI: 'Gold-i',
  PRIMEXM: 'PrimeXM',
  RUNALABS: 'Runa Labs',
}

export const APP_COMPANY_FAVICON_PATHS: Record<Exclude<AppCompany, 'DORA'>, string> = {
  BONDSWAP: '/assets/favicons/bondswap.png',
  BONDTRADER: '/assets/favicons/bondtrader.png',
  NAVION: '/assets/favicons/navion.png',
  ALLFX: '/assets/favicons/allfx.png',
  BLKBOX: '/assets/favicons/blkbox.png',
  PAI2: '/assets/favicons/pai2.png',
  MAXOS: '/assets/favicons/maxos.png',
  POLYTRADE: '/assets/favicons/polytrade.png',
  AFRICATRADES: '/assets/favicons/africatrades.png',
  GOLDI: '/favicon.png',
  PRIMEXM: '/favicon.png',
  RUNALABS: '/assets/favicons/runalabs.svg',
}

export const APP_COMPANY_LOGO_PATHS = {
  NAVION: {
    light: '/assets/images/branding/navion/logo.png',
    dark: '/assets/images/branding/navion/logo_dark.png',
  },
} satisfies Partial<Record<AppCompany, { light: string; dark: string }>>

export const APP_COMPANY_META_PIXEL_IDS: Partial<Record<AppCompany, string>> = {
  BONDTRADER: '2722814751453710',
}

const APP_COMPANY_SET = new Set<string>(APP_COMPANIES)
const TRADING_PROGRAM_APP_COMPANY_SET = new Set<AppCompany>(['BONDTRADER', 'DORA'])
const LENDING_APP_COMPANY_SET = new Set<AppCompany>(['DORA', 'BLKBOX', 'NAVION', 'BONDTRADER'])

export const normalizeAppCompany = (value: unknown): AppCompany => {
  if (typeof value !== 'string') return DEFAULT_APP_COMPANY

  const normalized = value.trim().toUpperCase()

  return APP_COMPANY_SET.has(normalized) ? (normalized as AppCompany) : DEFAULT_APP_COMPANY
}

export const getAppCompanyCssKey = (value: unknown): Lowercase<AppCompany> =>
  normalizeAppCompany(value).toLowerCase() as Lowercase<AppCompany>

export const isTradingProgramAppCompany = (value: unknown): boolean =>
  TRADING_PROGRAM_APP_COMPANY_SET.has(normalizeAppCompany(value))

export const isLendingAppCompany = (value: unknown): boolean =>
  LENDING_APP_COMPANY_SET.has(normalizeAppCompany(value))

export const getAppCompanyBrowserTitle = (value: unknown, doraTitle: string): string => {
  const company = normalizeAppCompany(value)

  return company === DEFAULT_APP_COMPANY ? doraTitle : APP_COMPANY_DISPLAY_NAMES[company]
}

export const getAppCompanyFaviconPath = (value: unknown, doraPath: string): string => {
  const company = normalizeAppCompany(value)

  return company === DEFAULT_APP_COMPANY ? doraPath : APP_COMPANY_FAVICON_PATHS[company]
}

export function getAppCompanyMetaPixelId(value: unknown): string | undefined {
  return APP_COMPANY_META_PIXEL_IDS[normalizeAppCompany(value)]
}
